// Modal de criar/editar usuário — mockups "Modal Usuário Geral",
// "· Permissões" e "· Segurança": casca AerosDialog com abas ilustradas,
// permissões em cards com switch (no lugar dos Selects enabled/disabled) e a
// aba Segurança com 2FA + dispositivos confiáveis.
//
// A lógica de carga e gravação foi preservada, incluindo as conversões que o
// backend espera (`finalizacaoComValorVendaAtiva` como booleano no PUT,
// `allowSeeMessagesInPendingTickets` normalizado, data de nascimento).
import React, { useState, useEffect, useContext, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles, alpha, useTheme } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import InputAdornment from "@material-ui/core/InputAdornment";
import CircularProgress from "@material-ui/core/CircularProgress";

import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import VpnKeyOutlinedIcon from "@material-ui/icons/VpnKeyOutlined";
import SecurityIcon from "@material-ui/icons/Security";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import CloseIcon from "@material-ui/icons/Close";
import LogoutIcon from "@material-ui/icons/ExitToApp";
import DevicesIcon from "@material-ui/icons/Devices";

import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import SpaceDashboardOutlinedIcon from "@mui/icons-material/SpaceDashboardOutlined";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import SyncAltOutlinedIcon from "@mui/icons-material/SyncAltOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import useWhatsApps from "../../hooks/useWhatsApps";
import { useDate } from "../../hooks/useDate";

import { Can } from "../Can";
import { getBackendUrl } from "../../config";
import AvatarUploader from "../AvatarUpload";
import TwoFactorSetup from "../TwoFactorSetup";
import AerosDialog from "../AerosDialog";
import AerosConfirmModal from "../AerosConfirmModal";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import {
  permissionGroups,
  permissionValue,
  countActivePermissions,
} from "../../pages/Users/permissionRules";

const backendUrl = getBackendUrl();

// Mapa nome-do-ícone (definido em permissionRules) → componente.
const PERMISSION_ICONS = {
  inbox: InboxOutlinedIcon,
  groups: GroupsOutlinedIcon,
  history: HistoryOutlinedIcon,
  visibility: VisibilityOutlinedIcon,
  taskAlt: TaskAltOutlinedIcon,
  paid: PaidOutlinedIcon,
  dashboard: SpaceDashboardOutlinedIcon,
  contacts: ContactsOutlinedIcon,
  campaign: CampaignOutlinedIcon,
  flow: AccountTreeOutlinedIcon,
  connections: SyncAltOutlinedIcon,
  realTime: MonitorHeartOutlinedIcon,
  forum: ForumOutlinedIcon,
};

const formatDateForInput = (date) => {
  if (!date) return "";

  // Se já está no formato YYYY-MM-DD, retorna como está
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }

  // Se é uma data ISO ou objeto Date, converte para YYYY-MM-DD
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";

  // Usar métodos locais para evitar problemas de timezone
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDateFromInput = (dateString) => {
  if (!dateString) return null;

  // Se já está no formato YYYY-MM-DD, retorna como está
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }

  // Se é uma data ISO, extrai apenas a parte da data
  if (dateString.includes("T")) {
    return dateString.split("T")[0];
  }

  return dateString;
};

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const borderInput = isLight
    ? theme.palette.aeros.borderStrong
    : "rgba(255,255,255,0.1)";
  return {
    // ---- cabeçalho com avatar + abas ----
    headerTop: { display: "flex", alignItems: "center", gap: 14 },
    avatarWrap: { position: "relative", flex: "none" },
    headerTitle: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 16,
      fontWeight: 700,
      letterSpacing: "-0.01em",
      color: theme.palette.text.primary,
    },
    headerMeta: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      marginTop: 2,
      display: "flex",
      alignItems: "center",
      gap: 6,
      flexWrap: "wrap",
    },
    removePhoto: {
      color: stateText(theme, AEROS.error, "error"),
      background: "none",
      border: "none",
      padding: 0,
      cursor: "pointer",
      font: "inherit",
      fontSize: 12,
      "&:hover": { textDecoration: "underline" },
    },
    tabsRow: { display: "flex", gap: 4, marginTop: 16 },
    tab: {
      padding: "10px 16px",
      fontSize: 13,
      fontWeight: 600,
      display: "flex",
      gap: 6,
      alignItems: "center",
      cursor: "pointer",
      background: "none",
      border: "none",
      borderBottom: "2px solid transparent",
      fontFamily: "inherit",
      color: theme.palette.text.secondary,
      "&:hover": { color: theme.palette.text.primary },
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
    tabOn: {
      color: theme.palette.text.primary,
      borderBottom: "2px solid",
      borderImage: "linear-gradient(90deg,#A044FF,#22D3EE) 1",
    },

    // ---- segmented control ----
    segmented: {
      display: "flex",
      gap: 4,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderInput}`,
      borderRadius: 10,
      padding: 3,
    },
    segment: {
      flex: 1,
      textAlign: "center",
      fontSize: 12,
      fontWeight: 600,
      padding: "6px 0",
      borderRadius: 7,
      cursor: "pointer",
      border: "none",
      background: "none",
      fontFamily: "inherit",
      color: theme.palette.text.secondary,
    },
    segmentOn: {
      color: theme.palette.text.primary,
      backgroundColor: alpha("#A044FF", 0.25),
    },
    hint: {
      fontSize: 11,
      color: theme.palette.aeros.textTertiary,
      marginTop: 5,
    },

    // ---- permissões ----
    permGroup: { marginBottom: 14 },
    permTitle: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      marginBottom: 8,
    },
    permGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 8,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    permCard: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 10,
      padding: "4px 6px 4px 12px",
      minHeight: 44,
      boxSizing: "border-box",
      "& > .MuiSvgIcon-root": { fontSize: 17, flexShrink: 0 },
    },
    permLabel: { flex: 1, fontSize: 12.5, fontWeight: 500 },
    permLabelOff: { color: theme.palette.text.secondary },

    // ---- segurança ----
    card: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 12,
      padding: "12px 14px",
      display: "flex",
      flexDirection: "column",
      gap: 8,
    },
    cardTitle: { fontSize: 12.5, fontWeight: 600 },
    deviceRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      fontSize: 12,
      "& > .MuiSvgIcon-root": {
        fontSize: 17,
        color: theme.palette.text.secondary,
        flexShrink: 0,
      },
    },
    deviceName: { color: theme.palette.text.primary, fontWeight: 500 },
    deviceMeta: { color: theme.palette.aeros.textTertiary },
    deviceRemove: {
      padding: 3,
      color: theme.palette.aeros.textTertiary,
      "&:hover": { color: stateText(theme, AEROS.error, "error") },
      "& .MuiSvgIcon-root": { fontSize: 15 },
    },
    sessionRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      borderRadius: 12,
      padding: "10px 14px",
      "& > .MuiSvgIcon-root": { fontSize: 18, color: theme.palette.text.secondary },
    },
    outlineSmall: {
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.16)"}`,
      color: theme.palette.text.primary,
      borderRadius: 8,
      padding: "5px 12px",
      fontSize: 12,
      fontWeight: 600,
      background: "none",
      cursor: "pointer",
      fontFamily: "inherit",
      "&:hover": { borderColor: alpha(AEROS.cyan, 0.5) },
    },
    footerMeta: { fontSize: 11.5, color: theme.palette.aeros.textTertiary },
  };
});

const UserSchema = Yup.object().shape({
  name: Yup.string().min(2, "Too Short!").max(50, "Too Long!").required("Required"),
  password: Yup.string().min(5, "Too Short!").max(50, "Too Long!"),
  email: Yup.string().email("Invalid email").required("Required"),
  allHistoric: Yup.string().nullable(),
});

const UserModal = ({ open, onClose, userId }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const theme = useTheme();
  const { datetimeToClient } = useDate();

  const initialState = {
    name: "",
    email: "",
    password: "",
    birthDate: "",
    profile: "user",
    startWork: "00:00",
    endWork: "23:59",
    farewellMessage: "",
    allTicket: "enable",
    allowGroup: false,
    defaultTheme: "light",
    defaultMenu: "open",
    allHistoric: "enabled",
    allUserChat: "enabled",
    userClosePendingTicket: "enabled",
    showDashboard: "enabled",
    allowRealTime: "enabled",
    allowConnections: "enabled",
    showContacts: "enabled",
    showCampaign: "enabled",
    showFlow: "enabled",
    finalizacaoComValorVendaAtiva: "false",
    allowSeeMessagesInPendingTickets: "enabled",
  };

  const { user: loggedInUser } = useContext(AuthContext);

  const [user, setUser] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [whatsappId, setWhatsappId] = useState(false);
  const { whatsApps } = useWhatsApps();
  const [profileUrl, setProfileUrl] = useState(null);
  const [tab, setTab] = useState("general");
  const [avatar, setAvatar] = useState(null);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [devices, setDevices] = useState([]);
  const [removingDevice, setRemovingDevice] = useState(null);
  const [confirmEndAll, setConfirmEndAll] = useState(false);
  const [createdAt, setCreatedAt] = useState(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return;
      try {
        const { data } = await api.get(`/users/${userId}`);
        setUser((prevState) => ({
          ...prevState,
          ...data,
          allTicket:
            data.allTicket === "enable" || data.allTicket === "enabled"
              ? "enable"
              : "disable",
          finalizacaoComValorVendaAtiva: data.finalizacaoComValorVendaAtiva
            ? "true"
            : "false",
          allowSeeMessagesInPendingTickets:
            data.allowSeeMessagesInPendingTickets === "enabled"
              ? "enabled"
              : "disabled",
          farewellMessage: data.farewellMessage || "",
          birthDate: formatDateForInput(data.birthDate),
        }));

        const { profileImage } = data;
        setProfileUrl(
          `${backendUrl}/public/company${data.companyId}/user/${profileImage}`
        );

        const userQueueIds = data.queues?.map((queue) => queue.id);
        setSelectedQueueIds(userQueueIds);
        setWhatsappId(data.whatsappId ? data.whatsappId : "");
        setTwoFactorEnabled(data.twoFactorEnabled || false);
        setCreatedAt(data.createdAt || null);
      } catch (err) {
        toastError(err);
      }
    };

    fetchUser();
  }, [userId, open]);

  // Dispositivos confiáveis: só existe endpoint para usuário já criado, e a
  // aba Segurança só aparece nesse caso.
  const loadDevices = async () => {
    if (!userId) return;
    try {
      const { data } = await api.get(`/users/${userId}/devices`);
      if (isMounted.current) setDevices(Array.isArray(data) ? data : []);
    } catch (err) {
      // Silencioso: um atendente sem permissão não deve ver toast de erro
      // ao abrir o próprio modal.
      if (isMounted.current) setDevices([]);
    }
  };

  useEffect(() => {
    if (open && userId && tab === "security") loadDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId, tab]);

  const handleClose = () => {
    onClose();
    setUser(initialState);
    setTab("general");
    setDevices([]);
    setShowPassword(false);
  };

  const handleSaveUser = async (values) => {
    const uploadAvatar = async (id) => {
      if (!avatar || typeof avatar !== "object") return null;

      const formData = new FormData();
      formData.append("userId", id);
      formData.append("typeArch", "user");
      formData.append("profileImage", avatar);

      try {
        const { data } = await api.post(`/users/${id}/media-upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        return data.user.profileImage;
      } catch (error) {
        console.error("Erro no upload da imagem:", error);
        throw error;
      }
    };

    const userData = {
      ...values,
      whatsappId,
      queueIds: selectedQueueIds,
      finalizacaoComValorVendaAtiva:
        values.finalizacaoComValorVendaAtiva === "true",
      birthDate: parseDateFromInput(values.birthDate),
      allowSeeMessagesInPendingTickets:
        values.allowSeeMessagesInPendingTickets === "enabled"
          ? "enabled"
          : "disabled",
    };

    try {
      let responseData;

      if (userId) {
        const { data } = await api.put(`/users/${userId}`, userData);
        responseData = data;

        if (avatar && typeof avatar === "object") {
          const newProfileImage = await uploadAvatar(userId);
          if (newProfileImage) {
            responseData.profileImage = newProfileImage;
            if (userId === loggedInUser.id) {
              localStorage.setItem("profileImage", newProfileImage);
            }
          }
        }
      } else {
        const { data } = await api.post("/users", userData);
        responseData = data.user;

        if (avatar && typeof avatar === "object" && responseData.id) {
          const newProfileImage = await uploadAvatar(responseData.id);
          if (newProfileImage) {
            responseData.profileImage = newProfileImage;
          }
        }
      }

      handleClose();
      toast.success(i18n.t("userModal.success"));

      if (userId === loggedInUser.id) {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handleRemoveDevice = async () => {
    const device = removingDevice;
    setRemovingDevice(null);
    if (!device) return;
    try {
      await api.delete(`/users/${userId}/devices/${device.id}`);
      toast.success(i18n.t("userModal.security.deviceRemoved"));
      loadDevices();
    } catch (err) {
      toastError(err);
    }
  };

  const handleEndAllSessions = async () => {
    setConfirmEndAll(false);
    try {
      await api.delete(`/users/${userId}/devices`);
      toast.success(i18n.t("userModal.security.endAllDone"));
      loadDevices();
    } catch (err) {
      toastError(err);
    }
  };

  const segmented = (value, options, onChange) => (
    <div className={classes.segmented} role="group">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          className={`${classes.segment} ${
            value === option.value ? classes.segmentOn : ""
          }`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  const field = (name, labelKey, { touched, errors, required, hint, ...extra } = {}) => (
    <div>
      <label className={m.fieldLabel} htmlFor={name}>
        {i18n.t(labelKey)}
        {required && <span style={{ color: AEROS.error, marginLeft: 3 }}>*</span>}
        {hint && (
          <span style={{ fontWeight: 500, opacity: 0.75, marginLeft: 4 }}>{hint}</span>
        )}
      </label>
      <Field
        as={TextField}
        id={name}
        name={name}
        variant="outlined"
        className={m.input}
        error={touched?.[name] && Boolean(errors?.[name])}
        helperText={(touched?.[name] && errors?.[name]) || ""}
        {...extra}
      />
    </div>
  );

  const renderGeneral = ({ touched, errors, values, setFieldValue }) => (
    <>
      <div className={m.grid2}>
        {field("name", "userModal.form.name", { touched, errors, required: true })}
        <div>
          <label className={m.fieldLabel} htmlFor="password">
            {i18n.t("userModal.form.password")}
            <span style={{ fontWeight: 500, opacity: 0.75, marginLeft: 4 }}>
              {userId ? i18n.t("userModal.passwordHint") : ""}
            </span>
          </label>
          <Field
            as={TextField}
            id="password"
            name="password"
            variant="outlined"
            className={m.input}
            type={showPassword ? "text" : "password"}
            error={touched.password && Boolean(errors.password)}
            helperText={(touched.password && errors.password) || ""}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label={i18n.t(
                      showPassword ? "login.hidePassword" : "login.showPassword"
                    )}
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>
      </div>

      <div className={m.grid2} style={{ gridTemplateColumns: "2fr 1fr" }}>
        {field("email", "userModal.form.email", { touched, errors, required: true })}
        <Can
          role={loggedInUser.profile}
          perform="user-modal:editProfile"
          yes={() => (
            <div>
              <span className={m.fieldLabel}>{i18n.t("userModal.form.profile")}</span>
              {segmented(
                values.profile,
                [
                  { value: "admin", label: i18n.t("userModal.profileAdmin") },
                  { value: "user", label: i18n.t("userModal.profileUser") },
                ],
                (v) => setFieldValue("profile", v)
              )}
            </div>
          )}
        />
      </div>

      <Can
        role={loggedInUser.profile}
        perform="user-modal:editQueues"
        yes={() => (
          <QueueSelect
            selectedQueueIds={selectedQueueIds}
            onChange={(selectedIds) => setSelectedQueueIds(selectedIds)}
          />
        )}
      />

      <div className={m.grid2} style={{ gridTemplateColumns: "1.4fr 1fr 1fr" }}>
        <Can
          role={loggedInUser.profile}
          perform="user-modal:editProfile"
          yes={() => (
            <div>
              <span className={m.fieldLabel}>{i18n.t("userModal.form.whatsapp")}</span>
              <Select
                variant="outlined"
                className={m.input}
                value={whatsappId || ""}
                onChange={(e) => setWhatsappId(e.target.value)}
                displayEmpty
                fullWidth
              >
                <MenuItem value="">&nbsp;</MenuItem>
                {whatsApps.map((whatsapp) => (
                  <MenuItem key={whatsapp.id} value={whatsapp.id}>
                    {whatsapp.name}
                  </MenuItem>
                ))}
              </Select>
            </div>
          )}
        />
        <Can
          role={loggedInUser.profile}
          perform="user-modal:editProfile"
          yes={() => (
            <>
              {field("startWork", "userModal.form.startWork", {
                touched,
                errors,
                type: "time",
                InputLabelProps: { shrink: true },
              })}
              {field("endWork", "userModal.form.endWork", {
                touched,
                errors,
                type: "time",
                InputLabelProps: { shrink: true },
              })}
            </>
          )}
        />
      </div>

      <div className={m.grid2} style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
        <div>
          {field("birthDate", "userModal.form.birthDate", {
            touched,
            errors,
            type: "date",
            InputLabelProps: { shrink: true },
          })}
          <div className={classes.hint}>{i18n.t("userModal.birthDateHint")}</div>
        </div>
        <div>
          <span className={m.fieldLabel}>{i18n.t("userModal.form.defaultTheme")}</span>
          {segmented(
            values.defaultTheme,
            [
              { value: "light", label: i18n.t("userModal.themeLight") },
              { value: "dark", label: i18n.t("userModal.themeDark") },
            ],
            (v) => setFieldValue("defaultTheme", v)
          )}
        </div>
        <div>
          <span className={m.fieldLabel}>{i18n.t("userModal.form.defaultMenu")}</span>
          {segmented(
            values.defaultMenu,
            [
              { value: "open", label: i18n.t("userModal.menuOpen") },
              { value: "closed", label: i18n.t("userModal.menuClosed") },
            ],
            (v) => setFieldValue("defaultMenu", v)
          )}
        </div>
      </div>

      {field("farewellMessage", "userModal.form.farewellMessage", {
        touched,
        errors,
        multiline: true,
        minRows: 3,
      })}
    </>
  );

  const renderPermissions = ({ values, setFieldValue }) => (
    <>
      {permissionGroups(values).map((group) => (
        <div key={group.title} className={classes.permGroup}>
          <div className={classes.permTitle}>{group.title}</div>
          <div className={classes.permGrid}>
            {group.items.map((item) => {
              const Icon = PERMISSION_ICONS[item.icon] || InboxOutlinedIcon;
              return (
                <div key={item.name} className={classes.permCard}>
                  <Icon
                    style={{
                      color: item.on
                        ? stateText(theme, AEROS.cyan, "info")
                        : theme.palette.aeros.textTertiary,
                    }}
                  />
                  <span
                    className={`${classes.permLabel} ${
                      item.on ? "" : classes.permLabelOff
                    }`}
                  >
                    {item.label}
                  </span>
                  <Switch
                    color="primary"
                    size="small"
                    checked={item.on}
                    onChange={(e) =>
                      setFieldValue(
                        item.name,
                        permissionValue(item.name, e.target.checked)
                      )
                    }
                    inputProps={{ "aria-label": item.label }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );

  const renderSecurity = () => (
    <>
      <TwoFactorSetup
        userId={userId}
        isAdmin={loggedInUser.profile === "admin"}
        isOwnProfile={userId === loggedInUser.id}
        twoFactorEnabled={twoFactorEnabled}
        onUpdate={(enabled) => setTwoFactorEnabled(enabled)}
      />

      <div className={classes.card}>
        <div className={classes.cardTitle}>
          {i18n.t("userModal.security.devices")}
        </div>
        {devices.length === 0 && (
          <div className={classes.deviceMeta} style={{ fontSize: 11.5 }}>
            {i18n.t("userModal.security.devicesEmpty")}
          </div>
        )}
        {devices.map((device) => (
          <div key={device.id} className={classes.deviceRow}>
            <DevicesIcon />
            <div style={{ flex: 1, minWidth: 0 }}>
              <span className={classes.deviceName}>
                {device.label || `#${device.id}`}
              </span>
              {device.lastUsedAt && (
                <span className={classes.deviceMeta}>
                  {" · "}
                  {i18n.t("userModal.security.lastUsed", {
                    when: datetimeToClient(device.lastUsedAt),
                  })}
                </span>
              )}
            </div>
            <Tooltip title={i18n.t("userModal.security.removeDevice")}>
              <IconButton
                className={classes.deviceRemove}
                aria-label={i18n.t("userModal.security.removeDevice")}
                onClick={() => setRemovingDevice(device)}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </div>
        ))}
      </div>

      <div className={classes.sessionRow}>
        <LogoutIcon />
        <div style={{ flex: 1, fontSize: 12.5, fontWeight: 600 }}>
          {i18n.t("userModal.security.endAllSessions")}
        </div>
        <button
          type="button"
          className={classes.outlineSmall}
          onClick={() => setConfirmEndAll(true)}
        >
          {i18n.t("userModal.security.endAll")}
        </button>
      </div>
    </>
  );

  const TABS = [
    { id: "general", labelKey: "userModal.tabs.general", Icon: PersonOutlineIcon },
    {
      id: "permissions",
      labelKey: "userModal.tabs.permissions",
      Icon: VpnKeyOutlinedIcon,
    },
    ...(userId
      ? [{ id: "security", labelKey: "userModal.tabs.security", Icon: SecurityIcon }]
      : []),
  ];

  return (
    <>
      <AerosConfirmModal
        open={Boolean(removingDevice)}
        onClose={() => setRemovingDevice(null)}
        onConfirm={handleRemoveDevice}
        icon={<CloseIcon />}
        tone="danger"
        title={i18n.t("userModal.security.removeDeviceTitle", {
          name: removingDevice?.label || "",
        })}
        description={i18n.t("userModal.security.removeDeviceText")}
        confirmLabel={i18n.t("userModal.security.removeDevice")}
      />
      <AerosConfirmModal
        open={confirmEndAll}
        onClose={() => setConfirmEndAll(false)}
        onConfirm={handleEndAllSessions}
        icon={<LogoutIcon />}
        tone="warning"
        title={i18n.t("userModal.security.endAllConfirmTitle", {
          name: user.name || "",
        })}
        description={i18n.t("userModal.security.endAllConfirmText")}
        confirmLabel={i18n.t("userModal.security.endAllSessions")}
      />

      <Formik
        initialValues={user}
        enableReinitialize={true}
        validationSchema={UserSchema}
        onSubmit={(values, actions) => {
          setTimeout(() => {
            handleSaveUser(values);
            actions.setSubmitting(false);
          }, 400);
        }}
      >
        {({ touched, errors, isSubmitting, values, setFieldValue, submitForm }) => {
          const counts = countActivePermissions(values);
          return (
            <AerosDialog
              open={open}
              onClose={handleClose}
              width={tab === "security" ? 560 : 760}
              title={i18n.t(userId ? "userModal.title.edit" : "userModal.title.add")}
              onSubmit={submitForm}
              loading={isSubmitting}
              submitLabel={i18n.t(
                userId ? "userModal.buttons.okEdit" : "userModal.buttons.okAdd"
              )}
              cancelLabel={i18n.t("userModal.buttons.cancel")}
              footerLeft={
                tab === "permissions" ? (
                  <span className={classes.footerMeta}>
                    {i18n.t("userModal.permissionCount", counts)}
                  </span>
                ) : createdAt ? (
                  <span className={classes.footerMeta}>
                    {i18n.t("userModal.footerMeta", {
                      created: datetimeToClient(createdAt),
                    })}
                  </span>
                ) : null
              }
              headerRight={null}
            >
              <Form style={{ display: "contents" }}>
                {/* cabeçalho próprio: avatar + identificação + abas */}
                <div>
                  <div className={classes.headerTop}>
                    <div className={classes.avatarWrap}>
                      <AvatarUploader
                        setAvatar={setAvatar}
                        avatar={user.profileImage}
                        companyId={user.companyId}
                      />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div className={classes.headerTitle}>
                        {values.name || i18n.t("userModal.title.add")}
                      </div>
                      <div className={classes.headerMeta}>
                        {userId ? (
                          <span>{i18n.t("userModal.idLabel", { id: userId })}</span>
                        ) : (
                          <span>{i18n.t("userModal.subtitleNew")}</span>
                        )}
                        {user.profileImage && (
                          <>
                            <span>·</span>
                            <button
                              type="button"
                              className={classes.removePhoto}
                              onClick={() => {
                                setFieldValue("profileImage", null);
                                setUser((prev) => ({ ...prev, profileImage: null }));
                                setAvatar(null);
                                setProfileUrl(null);
                              }}
                            >
                              {i18n.t("userModal.removePhoto")}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={classes.tabsRow} role="tablist">
                    {TABS.map(({ id, labelKey, Icon }) => (
                      <button
                        key={id}
                        type="button"
                        role="tab"
                        aria-selected={tab === id}
                        className={`${classes.tab} ${tab === id ? classes.tabOn : ""}`}
                        onClick={() => setTab(id)}
                      >
                        <Icon />
                        {i18n.t(labelKey)}
                      </button>
                    ))}
                  </div>
                </div>

                {tab === "general" &&
                  renderGeneral({ touched, errors, values, setFieldValue })}
                {tab === "permissions" && (
                  <Can
                    role={loggedInUser.profile}
                    perform="user-modal:editProfile"
                    yes={() => renderPermissions({ values, setFieldValue })}
                  />
                )}
                {tab === "security" && userId && renderSecurity()}
              </Form>
            </AerosDialog>
          );
        }}
      </Formik>
    </>
  );
};

export default UserModal;
